import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { AlertCircle, CheckCircle, Clock, Upload, X } from 'lucide-react';

interface SubscriptionStatus {
  id: string;
  planName: string;
  planType: string;
  priceVatu: number;
  startDate: string;
  endDate: string;
  daysRemaining: number;
  status: 'active' | 'expired' | 'grace_period' | 'locked';
  gracePeriodEnd?: string;
  memberCount?: number;
  perMemberPrice?: number;
  plan?: {
    billing_model?: string;
    price_per_member_vatu?: number;
  };
}

interface PaymentSubmission {
  amount: string;
  transactionDate: string;
  bankReference: string;
  paymentMethod: string;
  additionalNotes: string;
  receiptFile: File | null;
  receiptPreview: string | null;
}

interface SubscriptionManagementProps {
  groupId?: string | null;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ groupId: propGroupId }) => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [paymentForm, setPaymentForm] = useState<PaymentSubmission>({
    amount: '',
    transactionDate: new Date().toISOString().split('T')[0],
    bankReference: '',
    paymentMethod: 'bank_transfer',
    additionalNotes: '',
    receiptFile: null,
    receiptPreview: null
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [paymentReference, setPaymentReference] = useState<string>('');

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        console.log('[SubscriptionManagement] No user found');
        return;
      }

      console.log('[SubscriptionManagement] Fetching subscription - propGroupId:', propGroupId, 'user:', user.id, user.email);
      
      try {
        setLoading(true);

        let targetGroupId: string | null = null;
        let targetUserId: string = user.id; // Default to logged-in user

        // If groupId prop is provided, use it and get the group creator
        if (propGroupId) {
          targetGroupId = propGroupId;
          console.log('[SubscriptionManagement] Using provided groupId:', targetGroupId);
          
          // Get the group creator's user ID
          const { data: groupData, error: groupError } = await supabase
            .from('groups')
            .select('id, created_by')
            .eq('id', targetGroupId)
            .eq('is_active', true)
            .maybeSingle();

          if (groupError) {
            console.error('[SubscriptionManagement] Error fetching group:', groupError);
            throw groupError;
          }

          if (!groupData) {
            console.log('[SubscriptionManagement] Group not found:', targetGroupId);
            setSubscription(null);
            setLoading(false);
            return;
          }

          targetUserId = groupData.created_by;
          console.log('[SubscriptionManagement] Group creator:', targetUserId);
        } else {
          // If no groupId prop, get user's first active group
          const { data: groupsData, error: groupsError } = await supabase
            .from('groups')
            .select('id')
            .eq('created_by', user.id)
            .eq('is_active', true)
            .limit(1);

          if (groupsError) {
            console.error('[SubscriptionManagement] Error fetching groups:', groupsError);
            throw groupsError;
          }

          const groupData = groupsData?.[0];
          console.log('[SubscriptionManagement] Groups found:', groupsData?.length, 'First group:', groupData?.id);
          
          if (!groupData) {
            console.log('[SubscriptionManagement] No active groups found for user:', user.id);
            setSubscription(null);
            setLoading(false);
            return;
          }

          targetGroupId = groupData.id;
        }

        setCurrentGroupId(targetGroupId);
        console.log('[SubscriptionManagement] Using group:', targetGroupId, 'for user:', targetUserId);

        // Get group's subscription (including expired ones for pay-as-you-go)
        // Use maybeSingle() instead of single() to handle cases where no subscription exists
        let subscriptionData: any = null;
        let planData: any = null;
        
        // Get the subscription for the target user (group creator or logged-in user)
        const { data: subData, error: subError } = await supabase
          .from('user_subscriptions')
          .select(`
            id,
            start_date,
            end_date,
            payment_due_date,
            subscription_status,
            plan_id
          `)
          .eq('user_id', targetUserId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (subError) {
          console.error('[SubscriptionManagement] Error fetching subscription:', subError);
          throw subError;
        }
        
        subscriptionData = subData;
        console.log('[SubscriptionManagement] Subscription data:', subscriptionData);
        
        // If subscription exists, get the plan separately (in case nested select fails)
        if (subscriptionData?.plan_id) {
          const { data: planResult, error: planError } = await supabase
            .from('subscription_plans')
            .select('id, plan_name, plan_type, price_vatu, billing_model, price_per_member_vatu')
            .eq('id', subscriptionData.plan_id)
            .maybeSingle();
            
          if (planError) {
            console.error('[SubscriptionManagement] Error fetching plan:', planError);
          } else {
            planData = planResult;
            console.log('[SubscriptionManagement] Plan data:', planData);
          }
        }
        
        // Merge plan data into subscription data for compatibility
        if (subscriptionData && planData) {
          subscriptionData.plan = planData;
        }

        if (subscriptionData) {
          const plan = planData || subscriptionData.plan || {};
          const isPayAsYouGo = plan?.billing_model === 'per_member';
          
          if (!planData && !subscriptionData.plan) {
            console.warn('[SubscriptionManagement] Plan data not found for subscription:', subscriptionData.id);
          }
          
          // For pay-as-you-go: check payment_due_date, not end_date
          // For other plans: check end_date
          let daysRemaining = 0;
          let status: SubscriptionStatus['status'] = 'active';
          
          if (isPayAsYouGo && subscriptionData.payment_due_date) {
            // Pay-as-you-go: check payment_due_date
            const dueDate = new Date(subscriptionData.payment_due_date);
            const now = new Date();
            daysRemaining = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            
            // For pay-as-you-go, subscription is active even if payment is due
            // Status is based on subscription_status, not payment_due_date
            if (subscriptionData.subscription_status === 'expired') {
              status = 'expired';
            } else if (subscriptionData.subscription_status === 'locked') {
              status = 'locked';
            } else {
              status = 'active';
            }
          } else if (subscriptionData.end_date) {
            // Other plans: check end_date
            const endDate = new Date(subscriptionData.end_date);
            const now = new Date();
            daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            
            if (daysRemaining < 0) {
              status = subscriptionData.subscription_status === 'locked' ? 'locked' : 'expired';
            } else if (subscriptionData.subscription_status === 'locked') {
              status = 'locked';
            } else {
              status = 'active';
            }
          } else {
            // No end_date (pay-as-you-go with null end_date) - treat as active
            daysRemaining = 999; // Large number to show as active
            status = subscriptionData.subscription_status === 'expired' ? 'expired' : 'active';
          }

          // Calculate price based on billing model
          let calculatedPrice = parseFloat(plan?.price_vatu || 0);
          let memberCount = 0;
          let perMemberPrice = 0;
          
          // If pay-as-you-go, calculate based on member count
          if (plan?.billing_model === 'per_member' && targetGroupId) {
            // Get member count for this group - count members, clerks, admins (EXCLUDE group creator)
            // First try the database function (which excludes group creator)
            let memberCountData: number | null = null;
            const { data: rpcResult, error: rpcError } = await supabase
              .rpc('get_group_member_count', { group_id_param: targetGroupId });
            
            if (!rpcError && rpcResult !== null) {
              memberCountData = rpcResult;
              console.log('[SubscriptionManagement] RPC count result (excluding group creator):', memberCountData);
            } else {
              console.warn('[SubscriptionManagement] RPC failed, using direct query:', rpcError);
            }
            
            // Use RPC result if available
            if (memberCountData !== null) {
              memberCount = memberCountData;
            } else {
              // Fallback: direct query excluding group creator
              const { data: membersList, error: listError } = await supabase
                .from('group_members')
                .select('id, user_id, role')
                .eq('group_id', targetGroupId);
              
              // Get group creator ID to exclude from count
              const { data: groupInfo } = await supabase
                .from('groups')
                .select('created_by')
                .eq('id', targetGroupId)
                .maybeSingle();
              
              const groupCreatorId = groupInfo?.created_by;
              // Only count members, clerks, and admins (exclude group creator)
              memberCount = membersList?.filter(m => 
                m.user_id !== groupCreatorId && 
                ['member', 'clerk', 'admin'].includes(m.role)
              ).length || 0;
            }
            
            console.log('[SubscriptionManagement] Member count:', {
              groupId: targetGroupId,
              rpcCount: memberCountData,
              finalCount: memberCount
            });
            
            perMemberPrice = parseFloat(plan?.price_per_member_vatu || 0);
            calculatedPrice = perMemberPrice * memberCount;
            
            console.log('[SubscriptionManagement] Pay-as-you-go calculation:', {
              groupId: targetGroupId,
              memberCount,
              perMemberPrice,
              calculatedPrice,
              billing_model: plan?.billing_model,
              planId: plan?.id
            });
          }

          // For pay-as-you-go: use payment_due_date for display, not end_date
          // For other plans: use end_date
          const displayEndDate = isPayAsYouGo && subscriptionData.payment_due_date 
            ? subscriptionData.payment_due_date 
            : subscriptionData.end_date;
          
          const subscriptionState = {
            id: subscriptionData.id,
            planName: plan?.plan_name || 'Unknown',
            planType: plan?.plan_type || 'free',
            priceVatu: calculatedPrice,
            startDate: subscriptionData.start_date,
            endDate: displayEndDate || subscriptionData.end_date, // Use payment_due_date for pay-as-you-go
            daysRemaining,
            status,
            memberCount: plan?.billing_model === 'per_member' ? memberCount : undefined,
            perMemberPrice: plan?.billing_model === 'per_member' ? perMemberPrice : undefined,
            plan: {
              billing_model: plan?.billing_model,
              price_per_member_vatu: plan?.price_per_member_vatu
            }
          };
          
          console.log('[SubscriptionManagement] Setting subscription state:', subscriptionState);
          setSubscription(subscriptionState);

          // Generate payment reference
          if (targetGroupId) {
            try {
              const { data: refData, error: refError } = await supabase
                .rpc('generate_payment_reference', { group_id_param: targetGroupId });
              
              if (!refError && refData) {
                setPaymentReference(refData);
              } else {
                // Fallback reference generation
                const year = new Date().getFullYear();
                const groupCode = targetGroupId.substring(0, 3).toUpperCase();
                setPaymentReference(`CP-${groupCode}-${year}-001`);
              }
            } catch (error) {
              // Fallback reference generation
              const year = new Date().getFullYear();
              const groupCode = targetGroupId.substring(0, 3).toUpperCase();
              setPaymentReference(`CP-${groupCode}-${year}-001`);
            }
          }
        } else {
          console.log('[SubscriptionManagement] No subscription data found for user:', user.id);
          setSubscription(null);
        }

      } catch (error) {
        console.error('[SubscriptionManagement] Error fetching subscription:', error);
        setSubscription(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user, propGroupId]);

  const handleReceiptUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors({ receipt: 'Please upload an image file' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors({ receipt: 'File size must be less than 5MB' });
      return;
    }

    setErrors({ receipt: '' });
    setPaymentForm(prev => ({ ...prev, receiptFile: file }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPaymentForm(prev => ({ ...prev, receiptPreview: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentGroupId || !subscription) return;

    // Validate form
    const newErrors: Record<string, string> = {};
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    if (!paymentForm.transactionDate) {
      newErrors.transactionDate = 'Please select transaction date';
    }
    if (!paymentForm.bankReference.trim()) {
      newErrors.bankReference = 'Please enter bank reference';
    }
    if (!paymentForm.receiptFile) {
      newErrors.receipt = 'Please upload payment receipt';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);

    try {
      // Upload receipt to Supabase Storage
      const receiptFileName = `${user.id}/${Date.now()}-${paymentForm.receiptFile.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('payment-receipts')
        .upload(receiptFileName, paymentForm.receiptFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get signed URL (since bucket is private)
      const { data: urlData, error: urlError } = await supabase.storage
        .from('payment-receipts')
        .createSignedUrl(receiptFileName, 31536000); // 1 year expiry

      if (urlError) throw urlError;

      // Create payment record
      const { data: paymentData, error: paymentError } = await supabase
        .from('payment_records')
        .insert({
          subscription_id: subscription.id,
          user_id: user.id,
          group_id: currentGroupId,
          amount_vatu: parseFloat(paymentForm.amount),
          payment_method: paymentForm.paymentMethod,
          payment_reference: paymentReference,
          bank_reference: paymentForm.bankReference,
          transaction_date: paymentForm.transactionDate,
          receipt_url: receiptFileName, // Store path, not URL
          payment_proof_url: urlData.signedUrl,
          additional_notes: paymentForm.additionalNotes || null,
          tracking_id: paymentReference,
          payment_status: 'pending'
        })
        .select()
        .single();

      if (paymentError) throw paymentError;

      // Create notification for super_admin about payment submission
      try {
        const { data: superAdmins, error: superAdminError } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .eq('role', 'super_admin');

        if (!superAdminError && superAdmins && superAdmins.length > 0) {
          const notificationPromises = superAdmins.map(async (admin) => {
            const { error } = await supabase
              .from('notifications')
              .insert({
                user_id: admin.id,
                type: 'payment_submitted',
                title: 'New Payment Submitted',
                message: `Payment of ${parseFloat(paymentForm.amount).toLocaleString()} VT submitted by ${userProfile?.full_name || 'User'}. Reference: ${paymentReference}`,
                related_entity_id: paymentData.id,
                is_read: false,
              });

            if (error) {
              console.error(`Error creating notification for super admin ${admin.id}:`, error);
            }
          });

          await Promise.all(notificationPromises);
        }
      } catch (notifError) {
        console.error('Error creating super admin notifications for payment:', notifError);
      }

      // Create notification for group admin
      try {
        await supabase.from('notifications').insert({
          user_id: user.id,
          type: 'payment_pending',
          title: 'Payment Submitted',
          message: `Your payment of ${parseFloat(paymentForm.amount).toLocaleString()} VT has been submitted. Reference: ${paymentReference}. Please wait for approval.`,
          related_entity_id: paymentData.id,
          is_read: false,
        });
      } catch (notifError) {
        console.error('Error creating notification for group admin:', notifError);
      }

      setSubmitSuccess(true);
      setPaymentForm({
        amount: '',
        transactionDate: new Date().toISOString().split('T')[0],
        bankReference: '',
        paymentMethod: 'bank_transfer',
        additionalNotes: '',
        receiptFile: null,
        receiptPreview: null
      });
      setErrors({});

      setTimeout(() => {
        setShowPaymentForm(false);
        setSubmitSuccess(false);
      }, 3000);

    } catch (error: any) {
      console.error('Error submitting payment:', error);
      setErrors({ general: error.message || 'Failed to submit payment. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: SubscriptionStatus['status']) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10 border-success/20';
      case 'grace_period':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'expired':
      case 'locked':
        return 'text-destructive bg-destructive/10 border-destructive/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status: SubscriptionStatus['status']) => {
    switch (status) {
      case 'active':
        return 'CheckCircle';
      case 'grace_period':
        return 'Clock';
      case 'expired':
      case 'locked':
        return 'AlertCircle';
      default:
        return 'Info';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading subscription status...</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="bg-card border border-border rounded-md p-6">
        <div className="text-center py-8">
          <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Active Subscription</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Please contact support to set up your subscription.
          </p>
        </div>
      </div>
    );
  }

  // For pay-as-you-go plans, don't show expiry warning (subscription doesn't expire, only payment is due)
  const isPayAsYouGo = subscription.plan?.billing_model === 'per_member';
  const showExpiryWarning = !isPayAsYouGo && subscription.daysRemaining <= 7 && subscription.daysRemaining > 0;
  const isExpired = subscription.daysRemaining < 0;

  return (
    <div className="space-y-6">
      {/* Subscription Status Card */}
      <div className="bg-card border border-border rounded-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Subscription Status</h3>
            <p className="text-sm text-muted-foreground">Current plan and expiry information</p>
          </div>
          <div className={`px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(subscription.status)}`}>
            <div className="flex items-center gap-2">
              <Icon name={getStatusIcon(subscription.status)} size={16} />
              <span className="capitalize">{subscription.status.replace('_', ' ')}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Plan</p>
            <p className="text-sm font-semibold text-foreground">{subscription.planName}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Price</p>
            <p className="text-sm font-semibold text-foreground">
              {subscription.priceVatu.toLocaleString()} VT
              {subscription.memberCount !== undefined && subscription.perMemberPrice !== undefined && subscription.memberCount > 0 && (
                <span className="text-xs text-muted-foreground block mt-0.5 font-normal">
                  {subscription.memberCount} members × {subscription.perMemberPrice.toLocaleString()} VT
                </span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              {subscription.plan?.billing_model === 'per_member' ? 'Payment Due' : 'Expires'}
            </p>
            <p className="text-sm font-semibold text-foreground">
              {subscription.endDate && new Date(subscription.endDate).getTime() > 0
                ? new Date(subscription.endDate).toLocaleDateString()
                : 'N/A'}
            </p>
          </div>
        </div>

        {showExpiryWarning && (
          <div className="mt-4 p-4 bg-warning/10 border border-warning/20 rounded-md">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">
                  Subscription Expiring Soon
                </p>
                <p className="text-xs text-muted-foreground">
                  Your subscription expires in {subscription.daysRemaining} day{subscription.daysRemaining !== 1 ? 's' : ''}. 
                  Please renew to avoid service interruption.
                </p>
              </div>
            </div>
          </div>
        )}

        {subscription.status === 'grace_period' && subscription.gracePeriodEnd && (
          <div className="mt-4 p-4 bg-warning/10 border border-warning/20 rounded-md">
            <div className="flex items-start gap-3">
              <Icon name="Clock" size={20} color="var(--color-warning)" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">
                  Grace Period Active
                </p>
                <p className="text-xs text-muted-foreground">
                  Grace period ends on {new Date(subscription.gracePeriodEnd).toLocaleDateString()}. 
                  Limited functionality available.
                </p>
              </div>
            </div>
          </div>
        )}

        {isExpired && subscription.status === 'locked' && (
          <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">
                  Subscription Expired
                </p>
                <p className="text-xs text-muted-foreground">
                  Your subscription has expired. Please submit payment to restore full access.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Instructions */}
      <div className="bg-card border border-border rounded-md p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Payment Instructions</h3>
        <div className="space-y-3 text-sm">
          <div>
            <p className="font-medium text-foreground mb-1">Bank Details:</p>
            <p className="text-muted-foreground">BRED Bank - Innovatelhub Solutions Ltd</p>
            <p className="text-muted-foreground">Account: 013307727100019</p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">Payment Reference:</p>
            <p className="font-mono text-primary bg-primary/10 px-2 py-1 rounded inline-block">
              {paymentReference || 'Generating...'}
            </p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">Amount:</p>
            <p className="text-muted-foreground">{subscription.priceVatu.toLocaleString()} VT</p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">Payment Methods:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Bank app transfer</li>
              <li>Direct deposit at branch</li>
              <li>Mobile banking</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Payment Submission */}
      {!showPaymentForm ? (
        <Button
          onClick={() => setShowPaymentForm(true)}
          variant="default"
          iconName="Upload"
          iconPosition="left"
          fullWidth
        >
          Submit Payment Proof
        </Button>
      ) : (
        <div className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Submit Payment Proof</h3>
            <button
              onClick={() => {
                setShowPaymentForm(false);
                setPaymentForm({
                  amount: '',
                  transactionDate: new Date().toISOString().split('T')[0],
                  bankReference: '',
                  paymentMethod: 'bank_transfer',
                  additionalNotes: '',
                  receiptFile: null,
                  receiptPreview: null
                });
                setErrors({});
              }}
              className="p-2 hover:bg-muted rounded-md"
            >
              <X size={20} />
            </button>
          </div>

          {submitSuccess && (
            <div className="mb-4 p-4 bg-success/10 border border-success rounded-md">
              <div className="flex items-center gap-3">
                <CheckCircle size={20} color="var(--color-success)" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Payment Submitted Successfully
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Your payment is pending approval. Tracking ID: {paymentReference}
                  </p>
                </div>
              </div>
            </div>
          )}

          {errors.general && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive rounded-md">
              <p className="text-sm text-destructive">{errors.general}</p>
            </div>
          )}

          <form onSubmit={handleSubmitPayment} className="space-y-4">
            <Input
              type="number"
              label="Payment Amount (VT)"
              value={paymentForm.amount}
              onChange={(e) => {
                setPaymentForm(prev => ({ ...prev, amount: e.target.value }));
                setErrors(prev => ({ ...prev, amount: '' }));
              }}
              error={errors.amount}
              required
              placeholder={subscription.priceVatu.toString()}
            />

            <Input
              type="date"
              label="Transaction Date"
              value={paymentForm.transactionDate}
              onChange={(e) => {
                setPaymentForm(prev => ({ ...prev, transactionDate: e.target.value }));
                setErrors(prev => ({ ...prev, transactionDate: '' }));
              }}
              error={errors.transactionDate}
              required
            />

            <Input
              type="text"
              label="Bank Transaction Reference"
              value={paymentForm.bankReference}
              onChange={(e) => {
                setPaymentForm(prev => ({ ...prev, bankReference: e.target.value }));
                setErrors(prev => ({ ...prev, bankReference: '' }));
              }}
              error={errors.bankReference}
              required
              placeholder="Enter bank transaction reference"
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Payment Method
              </label>
              <select
                value={paymentForm.paymentMethod}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                className="w-full px-4 py-2 rounded-md border border-input bg-card text-foreground"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash_deposit">Cash Deposit</option>
                <option value="mobile_banking">Mobile Banking</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Payment Receipt *
              </label>
              <div className="border-2 border-dashed border-border rounded-md p-6 text-center">
                {paymentForm.receiptPreview ? (
                  <div className="space-y-3">
                    <img
                      src={paymentForm.receiptPreview}
                      alt="Receipt preview"
                      className="max-h-64 mx-auto rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentForm(prev => ({ ...prev, receiptFile: null, receiptPreview: null }));
                        setErrors(prev => ({ ...prev, receipt: '' }));
                      }}
                      className="text-sm text-destructive hover:text-destructive/80"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                    <label className="cursor-pointer">
                      <span className="text-sm text-primary hover:text-primary/80">
                        Click to upload or drag and drop
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleReceiptUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-muted-foreground mt-2">
                      PNG, JPG up to 5MB
                    </p>
                  </div>
                )}
              </div>
              {errors.receipt && (
                <p className="mt-1 text-sm text-destructive">{errors.receipt}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional Notes (Optional)
              </label>
              <textarea
                value={paymentForm.additionalNotes}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, additionalNotes: e.target.value }))}
                rows={3}
                className="w-full px-4 py-2 rounded-md border border-input bg-card text-foreground resize-none"
                placeholder="Any additional information about the payment..."
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPaymentForm(false)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={submitting}
                fullWidth
              >
                Submit Payment
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;

